import { inspectPdfBytes, type PdfSafetyReport } from "./pdf-safety";

/**
 * Upload guardrails for document analysis.
 *
 * A title commitment is a handful of pages. A 1,000-page scan is either a
 * mistake or an attempt to burn AI spend, so we reject it three times over:
 * in the browser (bytes, then real page count), and again on the server by
 * capping the encoded payload — a client can be bypassed, the server cannot.
 */

/** Largest PDF we accept, in bytes. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Largest document we will analyze, in pages. */
export const MAX_UPLOAD_PAGES = 60;

/**
 * Billing unit for document analysis. One report credit covers a 30-page
 * block; a longer document costs one more credit for each further block, so a
 * 31-page scan is two credits and a 60-page scan is two.
 */
export const PAGES_PER_CREDIT_BLOCK = 30;

/** Longest pasted / .txt Schedule B we will analyze, in characters. */
export const MAX_UPLOAD_TEXT_CHARS = 400_000;

/**
 * Server-side cap on the `data:application/pdf;base64,...` string. Base64 is
 * 4 chars per 3 bytes, plus the scheme prefix and a small slack allowance.
 */
export const MAX_PDF_DATA_URL_CHARS = Math.ceil((MAX_UPLOAD_BYTES * 4) / 3) + 1024;

export function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(bytes % (1024 * 1024) === 0 ? 0 : 1)} MB`;
}

/** One-line summary shown under the dropzones. */
export const UPLOAD_LIMITS_HINT = `PDF up to ${formatMegabytes(MAX_UPLOAD_BYTES)} and ${MAX_UPLOAD_PAGES} pages`;

/**
 * Intro sentence above the dropzones. Built from the same constants as the
 * hint so marketing copy can never advertise a different ceiling than the one
 * the uploader actually enforces.
 */
export const UPLOAD_INTRO_COPY = `Drag and drop a Title Commitment or Preliminary Title Report PDF. We accept files up to ${formatMegabytes(
  MAX_UPLOAD_BYTES,
)} and ${MAX_UPLOAD_PAGES} pages.`;


export const OVERSIZE_FILE_MESSAGE = `That file is larger than ${formatMegabytes(
  MAX_UPLOAD_BYTES,
)}. Upload just the title commitment, Schedule A and Schedule B, instead of the full closing package.`;

export function tooManyPagesMessage(pages: number, maxPages: number = MAX_UPLOAD_PAGES): string {
  return `That document is ${pages.toLocaleString()} pages. We analyze up to ${maxPages} pages. Upload just the title commitment (Schedule A and Schedule B) rather than the full closing package.`;
}

/** Cheap, synchronous checks: type and byte size. Returns an error message. */
export function checkUploadFile(file: File, options: { allowText?: boolean } = {}): string | null {
  const name = file.name.toLowerCase();
  const isPdf = file.type === "application/pdf" || name.endsWith(".pdf");
  const isText = file.type.startsWith("text/") || name.endsWith(".txt");
  if (!isPdf && !(options.allowText && isText)) {
    return options.allowText
      ? "Only .pdf and .txt title commitments are supported."
      : "Only PDF title commitments are supported.";
  }
  if (file.size > MAX_UPLOAD_BYTES) return OVERSIZE_FILE_MESSAGE;
  if (file.size === 0) return "That file is empty.";
  return null;
}

/** Everything we learned about a document during pre-flight. */
export type UploadInspection = {
  ok: boolean;
  fileName: string;
  byteSize: number;
  /** Page count when we could read it; null for .txt or an unreadable PDF. */
  pages: number | null;
  /** Report credits this document will cost (30-page blocks). */
  blocks: number;
  /** Structural counts, when the file is a PDF we could scan. */
  structure: PdfSafetyReport | null;
  /** Why we stopped, when `ok` is false. */
  rejection: { code: string; title: string; message: string } | null;
};

function reject(
  partial: Omit<UploadInspection, "ok" | "rejection">,
  code: string,
  title: string,
  message: string,
): UploadInspection {
  return { ...partial, ok: false, rejection: { code, title, message } };
}

/**
 * True page count of a PDF, read from its page tree without rasterizing.
 * Browser-only (pdf.js). Returns null when the file cannot be opened, which
 * the callers treat as "let the analyzer try" rather than a hard block.
 */
export async function readPdfPageCount(bytes: Uint8Array): Promise<number | null> {
  try {
    const pdfjs = await import("pdfjs-dist");
    const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    const doc = await pdfjs.getDocument({
      data: bytes,
      // Never run embedded scripts or fetch anything while counting pages.
      isEvalSupported: false,
      disableAutoFetch: true,
      disableFontFace: true,
    }).promise;
    const pages = doc.numPages;
    void doc.destroy();
    return pages;
  } catch {
    return null;
  }
}

/**
 * Full pre-flight for an uploaded document, in cost order: type and size,
 * then a raw structural scan, and only then pdf.js for the page count.
 *
 * Returns what we detected either way, so the UI can show the page count and
 * the specific cutoff reason instead of a bare "too big".
 */
export async function inspectUploadDocument(
  file: File,
  options: { allowText?: boolean; maxPages?: number; pagesPerBlock?: number } = {},
): Promise<UploadInspection> {
  const maxPages = options.maxPages ?? MAX_UPLOAD_PAGES;
  const pagesPerBlock = options.pagesPerBlock ?? PAGES_PER_CREDIT_BLOCK;
  const partial = {
    fileName: file.name,
    byteSize: file.size,
    pages: null as number | null,
    blocks: 1,
    structure: null as PdfSafetyReport | null,
  };

  const name = file.name.toLowerCase();
  const isPdf = file.type === "application/pdf" || name.endsWith(".pdf");
  const isText = file.type.startsWith("text/") || name.endsWith(".txt");

  if (!isPdf && !(options.allowText && isText)) {
    return reject(
      partial,
      "wrong_type",
      "Unsupported file type",
      options.allowText
        ? "Only .pdf and .txt title commitments are supported."
        : "Only PDF title commitments are supported.",
    );
  }
  if (file.size === 0) {
    return reject(partial, "empty", "Empty file", "That file is empty. Nothing to read.");
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return reject(partial, "too_large", "File is too large", OVERSIZE_FILE_MESSAGE);
  }
  if (!isPdf) return { ...partial, ok: true, rejection: null };

  const bytes = new Uint8Array(await file.arrayBuffer());
  const structure = inspectPdfBytes(bytes);
  const withStructure = { ...partial, structure };
  if (structure.issue) {
    return reject(
      withStructure,
      structure.issue.code,
      structure.issue.title,
      structure.issue.message,
    );
  }

  const pages = await readPdfPageCount(bytes);
  const withPages = {
    ...withStructure,
    pages,
    blocks: Math.max(1, Math.ceil((pages ?? 1) / pagesPerBlock)),
  };
  if (pages !== null && pages > maxPages) {
    return reject(
      withPages,
      "too_many_pages",
      "Too many pages",
      tooManyPagesMessage(pages, maxPages),
    );
  }
  if (pages === null) {
    return reject(
      withPages,
      "unreadable",
      "Couldn\u2019t open this PDF",
      "We couldn\u2019t read this PDF\u2019s pages. It may be damaged or use an unsupported format \u2014 try re-saving or printing it to a new PDF.",
    );
  }

  return { ...withPages, ok: true, rejection: null };
}

/** Back-compat wrapper: the first problem as a plain message, or null. */
export async function checkUploadDocument(
  file: File,
  options: { allowText?: boolean } = {},
): Promise<string | null> {
  const result = await inspectUploadDocument(file, options);
  return result.rejection?.message ?? null;
}
